import React from 'react';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import CategoryRoundedIcon from '@mui/icons-material/CategoryRounded';
import ExtensionRoundedIcon from '@mui/icons-material/ExtensionRounded';
import FormatListNumberedRoundedIcon from '@mui/icons-material/FormatListNumberedRounded';
import GestureRoundedIcon from '@mui/icons-material/GestureRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import ImageSearchRoundedIcon from '@mui/icons-material/ImageSearchRounded';
import QuizRoundedIcon from '@mui/icons-material/QuizRounded';
import SmartDisplayRoundedIcon from '@mui/icons-material/SmartDisplayRounded';
import SubtitlesRoundedIcon from '@mui/icons-material/SubtitlesRounded';
import TextFieldsRoundedIcon from '@mui/icons-material/TextFieldsRounded';
import type {
  PlanStudioBackendActivityType,
  PlanStudioBackendRuntimeTool,
  StudioGameOptionId,
  StudioOptionId,
} from './types';

export const OPTION_ORDER: StudioOptionId[] = [
  'generateImages',
  'generateVideo',
  'quiz',
  'oddWordOut',
  'oddPictureOut',
  'captionMatch',
  'categorySort',
  'sequenceOrder',
  'jigsawPuzzle',
  'memoryMatch',
  'letterTracing',
  'gapFill',
  'pronunciation',
  'clueDetective',
];

export const TOOL_OPTION_IDS: StudioOptionId[] = [
  'generateImages',
  'generateVideo',
];

export const GAME_OPTION_IDS: StudioGameOptionId[] = [
  'quiz',
  'oddWordOut',
  'oddPictureOut',
  'captionMatch',
  'categorySort',
  'sequenceOrder',
  'jigsawPuzzle',
  'memoryMatch',
  'letterTracing',
  'gapFill',
  'pronunciation',
  'clueDetective',
];

export const GAME_TO_API: Record<
  StudioGameOptionId,
  PlanStudioBackendActivityType
> = {
  quiz: 'quiz',
  oddWordOut: 'odd_word_out',
  oddPictureOut: 'odd_picture_out',
  captionMatch: 'caption_match',
  categorySort: 'category_sort',
  sequenceOrder: 'sequence_order',
  jigsawPuzzle: 'jigsaw_puzzle',
  memoryMatch: 'memory_match',
  letterTracing: 'letter_tracing',
  gapFill: 'gap_fill',
  pronunciation: 'pronunciation',
  clueDetective: 'clue_detective',
};

export const API_GAME_TO_UI: Record<
  PlanStudioBackendActivityType,
  StudioGameOptionId
> = {
  quiz: 'quiz',
  odd_word_out: 'oddWordOut',
  odd_picture_out: 'oddPictureOut',
  caption_match: 'captionMatch',
  category_sort: 'categorySort',
  sequence_order: 'sequenceOrder',
  jigsaw_puzzle: 'jigsawPuzzle',
  memory_match: 'memoryMatch',
  letter_tracing: 'letterTracing',
  gap_fill: 'gapFill',
  pronunciation: 'pronunciation',
  clue_detective: 'clueDetective',
};

export const GAME_REQUIRED_RUNTIME_TOOLS: Record<
  StudioGameOptionId,
  PlanStudioBackendRuntimeTool[]
> = {
  quiz: [],
  oddWordOut: [],
  oddPictureOut: ['generate_image'],
  captionMatch: ['generate_image'],
  categorySort: [],
  sequenceOrder: [],
  jigsawPuzzle: ['generate_image'],
  memoryMatch: ['generate_image'],
  letterTracing: [],
  gapFill: [],
  pronunciation: [],
  clueDetective: [],
};

export const isGameOption = (
  optionId: StudioOptionId
): optionId is StudioGameOptionId =>
  GAME_OPTION_IDS.includes(optionId as StudioGameOptionId);

export const OPTION_STYLES: Record<
  StudioOptionId,
  {
    chipClassName: string;
    panelClassName: string;
    iconClassName: string;
  }
> = {
  generateImages: {
    chipClassName: 'border-sky-200 bg-sky-100 text-sky-700',
    panelClassName:
      'border-sky-300 bg-linear-to-br from-sky-100 via-white to-sky-50 shadow-[0_18px_38px_rgba(14,165,233,0.18)] ring-1 ring-sky-100',
    iconClassName: 'text-sky-600',
  },
  generateVideo: {
    chipClassName: 'border-fuchsia-200 bg-fuchsia-100 text-fuchsia-700',
    panelClassName:
      'border-fuchsia-300 bg-linear-to-br from-fuchsia-100 via-white to-fuchsia-50 shadow-[0_18px_38px_rgba(217,70,239,0.16)] ring-1 ring-fuchsia-100',
    iconClassName: 'text-fuchsia-600',
  },
  quiz: {
    chipClassName: 'border-violet-200 bg-violet-100 text-violet-700',
    panelClassName:
      'border-violet-300 bg-linear-to-br from-violet-100 via-white to-violet-50 shadow-[0_18px_38px_rgba(139,92,246,0.17)] ring-1 ring-violet-100',
    iconClassName: 'text-violet-600',
  },
  oddWordOut: {
    chipClassName: 'border-pink-200 bg-pink-100 text-pink-700',
    panelClassName:
      'border-pink-300 bg-linear-to-br from-pink-100 via-white to-pink-50 shadow-[0_18px_38px_rgba(236,72,153,0.16)] ring-1 ring-pink-100',
    iconClassName: 'text-pink-600',
  },
  oddPictureOut: {
    chipClassName: 'border-pink-200 bg-pink-100 text-pink-700',
    panelClassName:
      'border-pink-300 bg-linear-to-br from-pink-100 via-white to-pink-50 shadow-[0_18px_38px_rgba(236,72,153,0.16)] ring-1 ring-pink-100',
    iconClassName: 'text-pink-600',
  },
  captionMatch: {
    chipClassName: 'border-purple-200 bg-purple-100 text-purple-700',
    panelClassName:
      'border-purple-300 bg-linear-to-br from-purple-100 via-white to-purple-50 shadow-[0_18px_38px_rgba(168,85,247,0.16)] ring-1 ring-purple-100',
    iconClassName: 'text-purple-600',
  },
  categorySort: {
    chipClassName: 'border-amber-200 bg-amber-100 text-amber-800',
    panelClassName:
      'border-amber-300 bg-linear-to-br from-amber-100 via-white to-amber-50 shadow-[0_18px_38px_rgba(245,158,11,0.16)] ring-1 ring-amber-100',
    iconClassName: 'text-amber-600',
  },
  sequenceOrder: {
    chipClassName: 'border-cyan-200 bg-cyan-100 text-cyan-700',
    panelClassName:
      'border-cyan-300 bg-linear-to-br from-cyan-100 via-white to-cyan-50 shadow-[0_18px_38px_rgba(6,182,212,0.16)] ring-1 ring-cyan-100',
    iconClassName: 'text-cyan-600',
  },
  jigsawPuzzle: {
    chipClassName: 'border-indigo-200 bg-indigo-100 text-indigo-700',
    panelClassName:
      'border-indigo-300 bg-linear-to-br from-indigo-100 via-white to-indigo-50 shadow-[0_18px_38px_rgba(99,102,241,0.16)] ring-1 ring-indigo-100',
    iconClassName: 'text-indigo-600',
  },
  memoryMatch: {
    chipClassName: 'border-rose-200 bg-rose-100 text-rose-700',
    panelClassName:
      'border-rose-300 bg-linear-to-br from-rose-100 via-white to-rose-50 shadow-[0_18px_38px_rgba(244,63,94,0.16)] ring-1 ring-rose-100',
    iconClassName: 'text-rose-600',
  },
  letterTracing: {
    chipClassName: 'border-teal-200 bg-teal-100 text-teal-700',
    panelClassName:
      'border-teal-300 bg-linear-to-br from-teal-100 via-white to-teal-50 shadow-[0_18px_38px_rgba(20,184,166,0.16)] ring-1 ring-teal-100',
    iconClassName: 'text-teal-600',
  },
  pronunciation: {
    chipClassName: 'border-sky-200 bg-sky-100 text-sky-700',
    panelClassName:
      'border-sky-300 bg-linear-to-br from-sky-100 via-white to-sky-50 ring-1 ring-sky-100',
    iconClassName: 'text-sky-600',
  },
  clueDetective: {
    chipClassName: 'border-cyan-200 bg-cyan-100 text-cyan-700',
    panelClassName: 'border-cyan-300 bg-linear-to-br from-cyan-100 via-white to-cyan-50 ring-1 ring-cyan-100',
    iconClassName: 'text-cyan-600',
  },
  gapFill: {
    chipClassName: 'border-emerald-200 bg-emerald-100 text-emerald-700',
    panelClassName:
      'border-emerald-300 bg-linear-to-br from-emerald-100 via-white to-emerald-50 shadow-[0_18px_38px_rgba(16,185,129,0.16)] ring-1 ring-emerald-100',
    iconClassName: 'text-emerald-600',
  },
};

export const renderOptionIcon = (
  optionId: StudioOptionId,
  className: string
): React.ReactNode => {
  switch (optionId) {
    case 'generateImages':
      return <ImageSearchRoundedIcon className={className} />;
    case 'generateVideo':
      return <SmartDisplayRoundedIcon className={className} />;
    case 'quiz':
      return <QuizRoundedIcon className={className} />;
    case 'oddWordOut':
      return <QuizRoundedIcon className={className} />;
    case 'oddPictureOut':
      return <ImageSearchRoundedIcon className={className} />;
    case 'captionMatch':
      return <SubtitlesRoundedIcon className={className} />;
    case 'categorySort':
      return <CategoryRoundedIcon className={className} />;
    case 'sequenceOrder':
      return <FormatListNumberedRoundedIcon className={className} />;
    case 'jigsawPuzzle':
      return <ExtensionRoundedIcon className={className} />;
    case 'memoryMatch':
      return <GridViewRoundedIcon className={className} />;
    case 'letterTracing':
      return <GestureRoundedIcon className={className} />;
    case 'pronunciation':
      return <MicRoundedIcon className={className} />;
    case 'clueDetective':
      return <ImageSearchRoundedIcon className={className} />;
    case 'gapFill':
      return <TextFieldsRoundedIcon className={className} />;
    default:
      return null;
  }
};

export const shouldShowOptionBadge = (_optionId: StudioOptionId): boolean =>
  false;
